import { useMemo, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { HighlightText } from '@/components/common/HighlightText';
import { SearchBar } from '@/components/common/SearchBar';
import { useColorMode } from '@/hooks/useColorMode';
import { useDensity } from '@/hooks/useDensity';
import {
  contrastReport,
  DEFAULT_PRESET_ID,
  getPreset,
  type DensityPreference,
  type ThemePreset,
} from '@/theme';
import { ThemePreview } from './ThemePreview';

interface ThemeDrawerProps {
  open: boolean;
  onClose: () => void;
}

function Section({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper sx={{ p: 2, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 1.5,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        <Typography variant="h5" component="h3">
          {title}
        </Typography>
        {actions}
      </Stack>
      {children}
    </Paper>
  );
}

const DENSITY_OPTIONS: { value: DensityPreference; label: string; hint: string }[] = [
  { value: 'auto', label: 'Auto', hint: 'Comfortable, or compact on large screens' },
  { value: 'comfortable', label: 'Comfortable', hint: 'Roomy rows, easy to tap' },
  { value: 'compact', label: 'Compact', hint: 'Tighter rows and cards' },
  { value: 'dense', label: 'Dense', hint: 'Most rows per screen, for data-heavy work' },
];

const ratio = (n: number) => `${n.toFixed(2)}:1`;

function contrastChip(value: number) {
  if (value >= 4.5) return <Chip size="small" variant="outlined" color="success" label="AA" />;
  if (value >= 3)
    return <Chip size="small" variant="outlined" color="warning" label="Large text only" />;
  return <Chip size="small" variant="outlined" color="error" label="Fail" />;
}

/**
 * Theme picker: a searchable preset gallery beside a live preview of the
 * highlighted preset, its palette, a type sample and a WCAG contrast report.
 * Browsing only previews — nothing changes until "Apply theme".
 */
export function ThemeDrawer({ open, onClose }: ThemeDrawerProps) {
  const muiTheme = useTheme();
  const isWide = useMediaQuery(muiTheme.breakpoints.up('lg'));
  const { mode, toggle, presetId, setPresetId, presets } = useColorMode();
  const {
    density,
    preference: densityPreference,
    setPreference: setDensityPreference,
  } = useDensity();

  // The previewed preset resets to the applied one each time the drawer opens,
  // so an abandoned browse never lingers as a pending change.
  const [selectedId, setSelectedId] = useState(presetId);
  const [selectedDensity, setSelectedDensity] = useState<DensityPreference>(densityPreference);
  const [search, setSearch] = useState('');
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSelectedId(presetId);
      setSelectedDensity(densityPreference);
      setSearch('');
    }
  }

  const allCount = useMemo(() => presets.reduce((n, g) => n + g.presets.length, 0), [presets]);

  // Name, description and category are all searchable — "dark" lives in a category name.
  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return presets;
    return presets
      .map((g) => ({
        ...g,
        presets: g.presets.filter(
          (p) =>
            p.meta.name.toLowerCase().includes(term) ||
            p.meta.description.toLowerCase().includes(term) ||
            p.meta.category.toLowerCase().includes(term),
        ),
      }))
      .filter((g) => g.presets.length > 0);
  }, [presets, search]);

  const selected: ThemePreset = getPreset(selectedId);
  const previewTokens = selected[mode];
  const report = useMemo(() => contrastReport(selected, mode), [selected, mode]);

  const presetDirty = selectedId !== presetId;
  const densityDirty = selectedDensity !== densityPreference;
  const isDirty = presetDirty || densityDirty;
  const isDefault = presetId === DEFAULT_PRESET_ID && densityPreference === 'auto';

  const apply = () => {
    setPresetId(selectedId);
    setDensityPreference(selectedDensity);
    onClose();
  };

  const restoreDefault = () => {
    setPresetId(DEFAULT_PRESET_ID);
    setSelectedId(DEFAULT_PRESET_ID);
    setDensityPreference('auto');
    setSelectedDensity('auto');
  };

  const gallery = (
    <Stack spacing={2.5}>
      <SearchBar
        placeholder={`Search ${allCount} themes`}
        label="Search themes"
        value={search}
        onChange={setSearch}
      />

      {filteredGroups.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
          Nothing matches “{search.trim()}”.
        </Typography>
      )}

      {filteredGroups.map((group) => (
        <Box key={group.category} component="section" aria-label={group.category}>
          <Stack
            direction="row"
            sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              {group.category}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {group.presets.length}
            </Typography>
          </Stack>

          <Stack spacing={1} role="listbox" aria-label={`${group.category} themes`}>
            {group.presets.map((p) => {
              const isSelected = p.meta.id === selectedId;
              const isApplied = p.meta.id === presetId;
              const tk = p[mode];
              return (
                <Box
                  key={p.meta.id}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  onClick={() => setSelectedId(p.meta.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedId(p.meta.id);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    // Outlined, not filled: a tinted selection would misrepresent the swatches.
                    boxShadow: isSelected ? `0 0 0 1px ${muiTheme.palette.primary.main}` : 'none',
                    bgcolor: 'background.paper',
                    transition: 'border-color .12s, box-shadow .12s',
                    '&:hover': { borderColor: 'primary.light' },
                    '&:focus-visible': {
                      outline: `2px solid ${muiTheme.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {/* Sidebar, brand, accent, surface — enough to recognise a preset. */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '2px',
                      width: 34,
                      height: 34,
                      borderRadius: 0.75,
                      overflow: 'hidden',
                      border: `1px solid ${muiTheme.palette.divider}`,
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ bgcolor: tk.component.sidebar.background }} />
                    <Box sx={{ bgcolor: tk.brand.primary.main }} />
                    <Box sx={{ bgcolor: tk.brand.accent.main }} />
                    <Box sx={{ bgcolor: tk.surface.paper }} />
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                        <HighlightText text={p.meta.name} query={search} />
                      </Typography>
                      {isApplied && (
                        <Chip size="small" variant="outlined" color="success" label="Applied" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      <HighlightText text={p.meta.description} query={search} />
                    </Typography>
                  </Box>

                  {isSelected && (
                    <CheckRoundedIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  const swatches: [string, string][] = [
    ['Primary', previewTokens.brand.primary.main],
    ['Secondary', previewTokens.brand.secondary.main],
    ['Accent', previewTokens.brand.accent.main],
    ['Canvas', previewTokens.surface.canvas],
    ['Paper', previewTokens.surface.paper],
    ['Border', previewTokens.border.default],
    ['Success', previewTokens.status.success.main],
    ['Warning', previewTokens.status.warning.main],
    ['Error', previewTokens.status.error.main],
  ];

  const contrastRows: [string, number][] = [
    ['Body text on card', report.bodyOnPaper],
    ['Secondary text on card', report.secondaryOnPaper],
    ['Label on primary button', report.onPrimary],
    ['Sidebar text', report.sidebarText],
  ];

  const previewPane = (
    <Stack spacing={2}>
      <Section
        title={selected.meta.name}
        actions={
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <Chip size="small" variant="outlined" label={selected.meta.category} />
            <Tooltip title={`Designed for ${selected.meta.designedFor} mode`}>
              <Chip
                size="small"
                variant="outlined"
                label={selected.meta.designedFor === 'dark' ? 'Dark-first' : 'Light-first'}
              />
            </Tooltip>
          </Stack>
        }
      >
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {selected.meta.description}
          </Typography>

          <ThemePreview tokens={previewTokens} />

          <Stack direction="row" spacing={0.75} useFlexGap sx={{ pt: 0.5, flexWrap: 'wrap' }}>
            {swatches.map(([label, value]) => (
              <Tooltip key={label} title={`${label} · ${value}`}>
                <Stack spacing={0.375} sx={{ width: 56, alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 26,
                      borderRadius: 0.75,
                      bgcolor: value,
                      border: `1px solid ${muiTheme.palette.divider}`,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 10 }}>
                    {label}
                  </Typography>
                </Stack>
              </Tooltip>
            ))}
          </Stack>

          {/* Type sample — a preset that changes the typeface is otherwise invisible until applied. */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: previewTokens.surface.sunken,
              border: `1px solid ${previewTokens.border.subtle}`,
              fontFamily: previewTokens.typography.fontFamily,
              color: previewTokens.text.primary,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                fontSize: previewTokens.typography.baseSize + 5,
                fontWeight: previewTokens.typography.weightBold,
                letterSpacing: `${previewTokens.typography.headingLetterSpacing}px`,
              }}
            >
              Term 1 attendance summary
            </Box>
            <Box
              sx={{
                fontSize: previewTokens.typography.baseSize,
                color: previewTokens.text.secondary,
                mt: 0.25,
              }}
            >
              Body text at {previewTokens.typography.baseSize}px, the size lists and forms are read
              at.
            </Box>
            <Box
              sx={{
                fontFamily: previewTokens.typography.monoFamily,
                fontSize: previewTokens.typography.baseSize - 1,
                mt: 0.75,
              }}
            >
              ADM-2026-0142 · Grade 7-B · 94.6%
            </Box>
          </Box>
        </Stack>
      </Section>

      <Section title="Density">
        <Stack spacing={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={selectedDensity}
            onChange={(_, next: DensityPreference | null) => {
              if (next) setSelectedDensity(next);
            }}
            aria-label="Density"
          >
            {DENSITY_OPTIONS.map((o) => (
              <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none' }}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {DENSITY_OPTIONS.find((o) => o.value === selectedDensity)?.hint}
            {selectedDensity === 'auto' ? ` — currently ${density}.` : '.'} Tables, lists, cards and
            page spacing follow this setting.
          </Typography>
        </Stack>
      </Section>

      <Section title="Accessibility">
        <Stack spacing={1}>
          {!report.passesAA && (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ alignItems: 'center', color: 'warning.main' }}
            >
              <WarningAmberRoundedIcon fontSize="small" />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Below WCAG AA in {mode} mode on at least one pair
              </Typography>
            </Stack>
          )}
          {contrastRows.map(([label, value]) => (
            <Stack
              key={label}
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
            >
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {ratio(value)}
                </Typography>
                {contrastChip(value)}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Section>
    </Stack>
  );

  const hint = presetDirty
    ? `${selected.meta.name} is previewed but not applied`
    : densityDirty
      ? 'Density change not applied yet'
      : isDefault
        ? 'Using the Camptain defaults'
        : `${selected.meta.name} is applied`;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Above the fixed app bar, which sits one layer over drawers.
      sx={{ zIndex: (t) => t.zIndex.modal }}
      slotProps={{
        paper: {
          'aria-labelledby': 'theme-drawer-title',
          sx: { width: isWide ? 'min(1080px, 94vw)' : 'min(720px, 100vw)', borderRadius: 0 },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {/* Header */}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            px: 2.5,
            py: 1.5,
            alignItems: 'center',
            bgcolor: 'var(--cf-drawer-header-bg)',
            color: 'var(--cf-drawer-header-text)',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="theme-drawer-title"
              variant="h4"
              component="h2"
              sx={{ color: 'inherit' }}
            >
              Theme
            </Typography>
            <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
              One theme drives every surface — navigation, tables, forms and dialogs.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={toggle}
            startIcon={mode === 'dark' ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
            sx={{ color: 'inherit', borderColor: 'currentColor', flexShrink: 0 }}
          >
            {mode === 'dark' ? 'Dark' : 'Light'}
          </Button>
          <IconButton onClick={onClose} aria-label="Close theme settings" sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />

        {isWide ? (
          // Two panes that scroll independently, so scrolling the list never drags the preview away.
          <Box sx={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
            <Box
              sx={{
                width: 340,
                flexShrink: 0,
                overflowY: 'auto',
                borderRight: 1,
                borderColor: 'divider',
                p: 2,
              }}
            >
              {gallery}
            </Box>
            <Box sx={{ flex: '1 1 auto', minWidth: 0, overflowY: 'auto', px: 2.5, py: 2 }}>
              {previewPane}
            </Box>
          </Box>
        ) : (
          // Narrow: one column, preview first.
          <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
            <Stack spacing={2.5}>
              {previewPane}
              <Divider />
              {gallery}
            </Stack>
          </Box>
        )}

        {/* Footer */}
        <Divider />
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ px: 2.5, py: 1.5, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: 1, minWidth: 160 }}
            role="status"
          >
            {hint}
          </Typography>
          <Button
            size="small"
            startIcon={<RestartAltRoundedIcon />}
            onClick={restoreDefault}
            disabled={isDefault}
          >
            Restore default
          </Button>
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={apply} disabled={!isDirty}>
            Apply theme
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
